<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import MinimalFooter from "~/layouts/components/MinimalFooter.vue";
import ScrollToTop from "~/components/utils/ScrollToTop.vue";
import CookieConsentContainer from "~/components/utils/CookieConsentContainer.vue";
import MinimalHeader from "~/layouts/components/MinimalHeader.vue";
import UploadDocument from "~/components/utils/UploadDocument.vue";
import {useDocumentStore} from "~/plugins/store/document";
import type {OcrResponse, TranslationList} from "~/types/Utils";
import {translation, useTranslationStore} from '~/plugins/store/translations';
import type {RcaInsuranceCompany} from "~/types/Rca";
import {useRcaStore} from "~/plugins/store/rca";
import {usePageStore} from "~/plugins/store/page";
import type {FaqCategory, FaqQuestion} from "~/types/Page";
import {useNuxtApp} from "#app";

export default defineComponent({
  components: {UploadDocument, MinimalHeader, CookieConsentContainer, ScrollToTop, MinimalFooter},
  setup() {
    useHead({
      title: 'Xiga | INSTANT.RO',
      meta: [
        {
          name: 'description',
          content: 'RCA online la cele mai bune prețuri prin INSTANT.RO'
        }
      ],
    });

    const documentStore = useDocumentStore();
    const documentList = ref(documentStore.documentList);
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const rcaStore = useRcaStore();
    const rcaInsuranceCompanies = ref<RcaInsuranceCompany[]>([]);
    const pageStore = usePageStore();
    const faqCategories = ref<FaqCategory[] | null>([]);
    const faqQuestions = ref<FaqQuestion[] | null>([]);
    const faqTab = ref("0");
    const {$toast} = useNuxtApp();

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      rcaInsuranceCompanies.value = await rcaStore.getAllRcaInsuranceCompanies();

      const fetchedFaqCategories = await pageStore.getFaqCategories();
      faqCategories.value = fetchedFaqCategories?.filter(c => c.code?.toLowerCase() === 'instant_ro') ?? null; // Păstrează doar AR

      faqQuestions.value = await pageStore.getQuestions();
    });

    const handleOcrResponse = async ({response, documentId}: { response: OcrResponse, documentId: number }) => {
      if (response && documentId) {
        navigateTo('/rca?partner_code=instantRo&ocr_data=' + encodeURIComponent(JSON.stringify(response)) + '&document_id=' + documentId);
      } else {
        $toast.error('Eroare la scanarea talonului auto, vă rugăm să introduceți datele manual.');
        navigateTo('/rca?partner_code=instantRo');
      }
    }

    const filteredQuestions = (categoryId: number) => {
      if (faqQuestions.value) {
        return faqQuestions.value.filter(q => q.category_id === categoryId);
      }
      return [];
    };

    return {
      documentList,
      translationList,
      translation,
      handleOcrResponse,
      rcaInsuranceCompanies,
      faqCategories,
      faqQuestions,
      faqTab,
      filteredQuestions
    }
  }
  });
</script>

<template>
  <div style="min-width: 350px;" class="fill-height d-flex flex-column">
    <div class="fill-height">
      <div class="d-flex justify-center py-2 border-1" style="background-color: #37B44B;" >
        <a href="/">
          <img src="/images/logo-white.webp" alt="Xiga" style="width: 100px; max-width: 100%"/>
        </a>
      </div>
      <div class="custom-header-instant" style="margin-top: -1px;">
        <v-container fluid class="component-container-960mw">
          <v-row class="d-flex justify-center">
            <v-col cols="12" sm="12" md="8" lg="6" xl="6" class="my-2">
              <div class="text-white py-2 font-family-bold fs-36">
                <b>RCA online la cele mai bune prețuri prin INSTANT.RO</b>
              </div>

              <div class="text-white fs-18px font-family-bold">
                <b>Totul in 3 pași simpli.</b>
              </div>
              <div class="rounded-pill px-1 py-1 my-2" style="background-color: #1E9B32;">
                <div class="d-flex justify-between align-center">
                  <div class="d-flex align-center">
                    <div class="circle">1</div>
                    <div class="px-2 fw-700">
                      {{ translation(translationList.complete_data_quickly, 'Completeaza rapid datele necesare') }}
                    </div>
                  </div>
                  <v-icon>mdi-chevron-right</v-icon>
                </div>
              </div>

              <div class="rounded-pill px-1 py-1 my-2" style="background-color: #21AA37;">
                <div class="d-flex justify-between align-center">
                  <div class="d-flex align-center">
                    <div class="circle">2</div>
                    <div class="px-2 fw-700">
                      {{ translation(translationList.compare_offers_for_your_car, 'Compara ofertele pentru masina ta') }}
                    </div>
                  </div>
                  <v-icon>mdi-chevron-right</v-icon>
                </div>
              </div>

              <div class="rounded-pill px-1 py-1 my-2" style="background-color: #21AA37;">
                <div class="d-flex justify-between align-center">
                  <div class="d-flex align-center">
                    <div class="circle">3</div>
                    <div class="px-2 fw-700">
                      {{ translation(translationList.buy_policy_from_anywhere, 'Cumpara polita de oriunde te-ai afla') }}
                    </div>
                  </div>
                  <v-icon>mdi-chevron-right</v-icon>
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="12" md="8" lg="6" xl="6" class="my-2">
              <v-card rounded="xl" variant="elevated" elevation="15" class="px-2">
                <v-card-text>
                  <div class="text-secondary fs-18px text-center py-2 font-family-semibold">
                    <img src="/images/misc/instant-ro-banner.png" alt="Instant.RO" style="width: 100%; max-width: 100%"/>
                  </div>

                  <div class="d-flex justify-center mx-4 my-2">
                    <img src="/images/misc/talon-banner.png" alt="Talon" class="talon-size"/>
                  </div>

                  <div class="text-dark fs-14px text-center">
                    Faci o poza talonului auto, compari si cumperi oriunde te-ai afla.
                  </div>

                  <div class="my-2 mt-4">
                    <upload-document
                        :txtPlaceholder="translation(translationList.which_car_to_insure, 'Scanează talonul')"
                        :documentId="documentList.vehicle_registration_id"
                        @ocr-response="handleOcrResponse"
                    >
                    </upload-document>
                  </div>
                  <div class="my-2">
                    <NuxtLink to="/rca?partner_code=instantRo">
                      <v-btn
                          color="primary"
                          variant="outlined"
                          size="x-large"
                          flat
                          class="block-on-mobile w-100"
                          rounded="lg"
                      >
                        {{ translation(translationList.add_manual_data, 'Sau adaugă datele manual') }}
                      </v-btn>
                    </NuxtLink>
                  </div>

                  <div class="text-dark text-center" style="font-size: 12px">
                    Continuând procesul de scanare, îți dai acordul pentru <a href="/info/terms-and-conditions" target="_blank">prelucrarea
                    datelor cu caracter personal</a> în scopul obținerii de oferte RCA
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- RCA Insurance Companies -->
      <v-container fluid class="component-container-full bg-grey-lighten-4">
        <v-container class="container">
          <div class="d-flex flex-row align-center justify-center my-5">
            <div class="fw-700 fs-30 mx-3 color-primary text-center">{{
                translation(translationList.partner_insurance_companies, "Companii de Asigurare Partenere")
              }}
            </div>
          </div>
          <v-row class="d-flex align-center justify-center mt-5">
            <div class="d-flex flex-wrap align-center justify-center pa-1">
              <v-col cols="auto" sm="auto" md="auto" class="pa-1"
                     v-for="(insuranceCompany, index) in rcaInsuranceCompanies"
                     :key="index">
                <v-card height="80" width="130" rounded="lg" flat color="ghost-white"
                        class="d-flex align-center justify-center">
                  <NuxtImg :src="'/images/companies/insurance/thumbnails/' + insuranceCompany.code + '.webp'"
                           :alt="insuranceCompany.name" class="pa-1 w-100 pulse animated"/>
                </v-card>
              </v-col>
            </div>
          </v-row>
        </v-container>
      </v-container>

      <!-- FAQ container -->
      <v-container v-if="faqCategories && faqCategories.length > 0" fluid class="component-container-full">
        <v-container class="container">
          <v-container class="component-container-800mw" style="min-height: 350px;">
            <div class="d-flex flex-column align-center justify-center">
              <div class="fw-700 fs-30 text-center">
                {{ translation(translationList.faq_title, "Întrebări frecvente") }}
              </div>
            </div>
            <div class="mt-10">
              <v-tabs
                  class="faq-tab"
                  v-model="faqTab"
                  fixed-tabs
                  center-active
                  show-arrows
              >
                <v-tab variant="outlined" class="fw-600 text-dark" selected-class="selected" rounded="xl"
                       v-for="(faqCategory, index) in faqCategories" :key="index">
                  {{ faqCategory.category }}
                </v-tab>
              </v-tabs>

              <v-tabs-window v-model="faqTab">
                <v-tabs-window-item
                    v-for="(faqCategory, index) in faqCategories"
                    :key="index"
                    :value="index"
                >
                  <v-expansion-panels class="my-5 border-solid-contrast">
                    <v-expansion-panel
                        bg-color="gray-light"
                        rounded="xl"
                        v-for="(faqQuestion, qIndex) in filteredQuestions(<number>faqCategory.id)"
                        :key="qIndex"
                    >
                      <template v-slot:title>
                        <div class="fw-700 fs-20">{{ faqQuestion.question }}</div>
                      </template>
                      <template v-slot:text>
                        <div class="fw-500 fs-16 mx-4">
                          {{ faqQuestion.answer }}
                        </div>
                      </template>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-tabs-window-item>
              </v-tabs-window>
            </div>
          </v-container>
        </v-container>
      </v-container>
    </div>
    <scroll-to-top></scroll-to-top>
    <cookie-consent-container/>
    <minimal-footer/>
  </div>
</template>

<style scoped>
.bg-lightgray {
  background-color: #EBEBEB;
}

.banner-size {
  max-width: 100%;
}

@media (max-width: 576px) {
  .talon-size {
    max-width: 80%;
  }
}

/* Adjust max-width for medium-sized screens */
@media (min-width: 576px) {
  .banner-size {
    max-width: 80%;
  }

  .talon-size {
    max-width: 80%;
  }
}

/* Adjust max-width for larger screens */
@media (min-width: 768px) {
  .banner-size {
    max-width: 50%;
  }

  .talon-size {
    max-width: 50%;
  }
}

@media (min-width: 1200px) {
  .banner-size {
    max-width: 35%;
  }

  .talon-size {
    max-width: 50%;
  }
}

.partner-max-height {
  min-height: 150px;
  max-height: 155px;
  padding-left: 5px;
  padding-right: 5px;
  margin-bottom: 5px;
  margin-top: 5px;
}

.partner-image-max-width {
  max-width: 200px;
}

@media (max-width: 575px) {
  .partner-max-height {
    min-height: 110px;
    max-height: 115px;
    padding-left: 6px !important;
    padding-right: 6px !important;
    margin-bottom: 6px !important;
    margin-top: 6px !important;
  }

  .partner-image-max-width {
    max-width: 130px;
  }
}
.circle {
  width: 40px;
  height: 40px;
  background-color: #37B44B;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-header-instant {
  position: relative;
  background-image: url('/images/misc/wave-instant.png');
  background-size: cover; /* Scale the image to fit the container */
  background-repeat: no-repeat; /* Prevent the image from repeating */
  color: #fff;
  padding: 20px;
}

@media (max-width: 767px) {
  .custom-header-instant {
    position: relative;
    background-image: url('/images/misc/wave-instant-mobile.png');
    background-size: cover;
    color: #fff; /* Text color for the footer */
    padding: 10px;
  }
}
</style>
